import { Util, devLog } from "utils";
import { HOST } from "consts/env";
import { FetchManager } from "services/FetchManager";
import {
  RoleCategoryType,
  RoleCreateFormType,
  RoleType,
  RoleUpdateFormType,
} from "types/admin/AdminType";
import { create } from "zustand";

export interface PermissionInfoType {}

export interface RoleStoreType {
  getRoleCategoryList: () => Promise<RoleCategoryType[]>;
  getRolesList: (sortOrder: string, search: string) => Promise<RoleType[]>;
  createRoles: (form: RoleCreateFormType) => Promise<any>;
  updateRoles: (form: RoleUpdateFormType, rolesId: string) => Promise<any>;
  deleteRoles: (rolesId: string) => Promise<boolean>;
}

const useRoleAPIStore = create<RoleStoreType>((set) => ({
  /**
   * @description 사용자가 제어할 수 있는 Role 목록을 가져옴
   * @author jh_jeong
   */
  async getRoleCategoryList() {
    try {
      const url = `${HOST}/roles/info`;
      const res = await FetchManager.get(url);

      const data = await Util.fetchAndSetData(res);

      // Category : [ Users , Roles , Library ] 등등에 탭에 대한 권한
      // Roles : [ test_up , Power User ,test_jh ] 등등 각 Role 권한들을 묶은 권한
      // Role : [ Create User , Full Access , View roles ] 등등 각 카테고리에 대한 권한 목록

      if (data.isSuccess && data.code === 200 && data.resultList) {
        // data.resultList : 모든 Category별 Role 목록.(단 자신이 가지고 있는 Category별 Role 이 한개도 없을시 해당 Category는 제외됨.)
        // categoryAccessIds : 사용자가 제어할 수 있는 role id 목록
        //-------------------------------------------------------------------------------------
        // controllableRoleList : Category 별 사용자가 가지고 있어 제어할 수 있는 role만 필터링한 목록
        const controllableRoleList = data.resultList.map(
          (item: RoleCategoryType) => {
            return {
              ...item,
              roles: item.roles.filter((role: any) => {
                return item.categoryAccessIds.includes(role.roleId);
              }),
            };
          }
        );

        return controllableRoleList;
      } else throw new Error();
    } catch (e) {
      devLog("[getRoleCategoryList] 오류 발생", e);
      return false;
    }
  },

  async getRolesList(sortOrder: string, search: string) {
    try {
      const url = `${HOST}/roles?sortOrder=${sortOrder}&search=${Util.encodeAndNormalize(
        search
      )}`;
      const res = await FetchManager.get(url);
      const data = await Util.fetchAndSetData(res);

      if (data.isSuccess && data.code === 200 && data.resultList)
        return data.resultList;
      else throw new Error();
    } catch (e) {
      devLog("[getRolesList] 오류 발생", e);
      return false;
    }
  },

  async createRoles(form: RoleCreateFormType) {
    try {
      const url = `${HOST}/roles`;
      const res = await FetchManager.post(url, {
        ...form,
        rolesName: Util.normalizeSearch(form.rolesName),
      });
      const data = await Util.fetchAndSetData(res);

      if (data) return data;
      else throw new Error();
    } catch (e) {
      devLog("[createRoles] 오류 발생", e);
      return false;
    }
  },

  async updateRoles(form: RoleUpdateFormType, rolesId: string) {
    try {
      const url = `${HOST}/roles/${rolesId}`;
      const res = await FetchManager.patch(url, {
        ...form,
        rolesName: Util.normalizeSearch(form.rolesName as string),
      });
      const data = await Util.fetchAndSetData(res);

      if (data) return data;
      else throw new Error();
    } catch (e) {
      devLog("[updateRoles] 오류 발생", e);
      return false;
    }
  },

  async deleteRoles(rolesId: string) {
    try {
      const url = `${HOST}/roles/${rolesId}`;
      const res = await FetchManager.delete(url);
      const data = await Util.fetchAndSetData(res);

      if (data.isSuccess && data.code === 200) return data.isSuccess;
      else throw new Error();
    } catch (e) {
      devLog("[deleteRoles] 오류 발생", e);
      return false;
    }
  },
}));

export default useRoleAPIStore;
